import React, { useState, useEffect, useRef, useCallback } from 'react';
import MapboxService from '../../../services/mapboxService';
import {debounce} from "../../../utils/common";

const DEBOUNCE_DELAY = 500; // milliseconds
const MIN_QUERY_LENGTH = 2;

const MapboxAutocomplete = ({ defaultValue, onPlaceSelect }) => {
  const [query, setQuery] = useState(defaultValue || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const mapboxService = useRef(new MapboxService()).current;
  const abortControllerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSelect = (feature) => {
    console.log(feature, 'feature');
    const transformed = {
      lat: feature.center[1],
      lng: feature.center[0],
      name: feature.place_name
    };
    onPlaceSelect(transformed);
    setQuery(feature.place_name);
    setShowDropdown(false);
    setResults([]);
  };

  // Create debounced search function with useCallback to maintain reference
  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length > MIN_QUERY_LENGTH) {
        // Cancel previous request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create new abort controller
        abortControllerRef.current = new AbortController();

        setLoading(true);
        try {
          const response = await mapboxService.geocodeForward({
            query: searchQuery,
            limit: 10,
            autocomplete: true
          });

          if (!abortControllerRef.current?.signal.aborted) {
            setResults(response.data.results || []);
            setShowDropdown(true);
            setLoading(false);
          }
        } catch (error) {
          if (!abortControllerRef.current?.signal.aborted) {
            console.error('Mapbox search error:', error);
            setResults([]);
            setLoading(false);
          }
        }
      } else {
        setResults([]);
        setShowDropdown(false);
        setLoading(false);
      }
    }, DEBOUNCE_DELAY),
    []
  );

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    debouncedSearch(newQuery);
  };

  return (
    <div className="mapbox-autocomplete">
      <input
        className="custom-input"
        type="text"
        placeholder="Type address"
        value={query}
        onChange={handleInputChange}
      />
      {loading && <div className="autocomplete-loading">Searching...</div>}
      {showDropdown && results.length > 0 && (
        <div className="autocomplete-dropdown">
          {results.map((result, idx) => (
            <div
              key={idx}
              className="autocomplete-item"
              onClick={() => handleSelect(result)}
            >
              {result.place_name}
            </div>
          ))}
        </div>
      )}
      {showDropdown && !loading && results.length === 0 && query.length > 2 && (
        <div className="autocomplete-dropdown">
          <div className="autocomplete-item no-results">No results found</div>
        </div>
      )}
    </div>
  );
};

export default MapboxAutocomplete;
